<template>
	<v-expansion-panel
		:value="compProps.instanceTask.id"
		rounded="lg"
		:style="{ 'border-left': `9px solid ${templateTask?.color || 'rgb(var(--v-theme-primary))'}` }"
		@group:selected="onGroupSelected"
	>
		<v-expansion-panel-title class="d-flex justify-center" style="height: 48px">
			<div class="d-flex align-center" style="width: 100%">
				<IconPreview :icon="templateTask?.context?.icon" />
				<div class="text-h6" style="line-height: 1.3rem; font-weight: 400">{{ title }}</div>
				<v-spacer></v-spacer>
				<v-tooltip :text="symbolsStore.GUILabel('0b82eea1-ef08-49de-958c-8b922d2ec65c')" location="top">
					<template v-slot:activator="{ props }">
						<v-btn
							v-if="
								domainStore.hasAccessRights(UserRole.Editor) &&
								(debugStore.altKeyHeld || debugStore.debugMode)
							"
							v-bind="props"
							size="small"
							color="blue"
							class="ml-1"
							icon="mdi-link"
							variant="text"
							@click="openDesignerProcess"
						>
						</v-btn>
					</template>
				</v-tooltip>

				<v-tooltip v-if="displayUnknownButton() && openQuestion" location="bottom" open-delay="750">
					<template v-slot:activator="{ props }">
						<v-btn
							v-bind="props"
							icon="mdi-skip-next-circle-outline"
							variant="text"
							@click="autofillUnknown"
						>
						</v-btn>
					</template>
					<span>{{ symbolsStore.GUILabel("3a7fc0da-9262-48c0-a0f1-e22dee555a72") }}</span>
				</v-tooltip>
				<v-tooltip
					v-if="displayUnknownButton() && !openQuestion && atLeastOneAttributeUnknown"
					location="bottom"
					open-delay="750"
				>
					<template v-slot:activator="{ props }">
						<v-btn
							v-bind="props"
							icon="mdi-skip-previous-circle-outline"
							variant="text"
							@click="unfillUnknown"
						>
						</v-btn>
					</template>
					<span>{{ symbolsStore.GUILabel("eb77d0a4-dc9e-40c0-937a-02945d984df0") }}</span>
				</v-tooltip>
			</div>
		</v-expansion-panel-title>
		<v-expansion-panel-text style="overflow: hidden">
			<div v-if="subtitle" class="d-flex justify-space-between align-center mb-2">
				<div style="font-size: 1rem" class="d-flex align-center">
					<AskQuestionButton
						v-if="displayPlayButton() && openQuestion"
						:color="templateTask?.color || 'rgb(var(--v-theme-primary))'"
						:text="
							rephrased_translated.length > 0
								? markdownToTxt(rephrased_translated)
								: compProps.instanceTask?.context?.question_body_txt?.[domainStore.languagePreference]
								? compProps.instanceTask?.context?.question_body_txt?.[domainStore.languagePreference]
								: subtitle
						"
					/>
					<div
						class="markdown-content"
						v-html="marked.parse(rephrased_translated.length > 0 ? rephrased_translated : subtitle)"
					></div>
				</div>
			</div>
			<div v-if="note">
				<p class="mt-2" style="font-style: italic; color: gray; margin-top: -10px; margin-bottom: 10px">
					{{ note }}
				</p>
			</div>
			<QuestionInput
				v-for="(attr, i) in outputAttributes"
				:key="attr.id"
				:instance-attribute-id="attr.id"
				:instance-task="compProps.instanceTask"
				:show-unknown-button="compProps.showUnknownButton"
				:style="{ 'margin-bottom': i < outputAttributes.length - 1 ? '10px' : '0px' }"
				@set="onSet"
			/>
		</v-expansion-panel-text>
	</v-expansion-panel>
</template>

<script setup lang="ts">
import { ref, computed, PropType, watch, onMounted, onUnmounted } from "vue";
import { useGraphStore } from "../../../store/graph";
import { useQuestionsStore } from "../../../store/questions";
import { useDomainStore } from "../../../store/domain";
import { useSymbolsStore } from "../../../store/symbols";
import { useDebugStore } from "../../../store/debug";
import { useSessionStore } from "../../../store/alternis/session";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import QuestionInput from "./QuestionInput.vue";
import { useQuestionHistoryStore } from "../../../store/questionHistory";
import { GraphModification, InstanceAttribute, InstanceTask, UserRole } from "@lr-notitia/notitia-operator/schemas";
import AskQuestionButton from "./AskQuestionButton.vue";
import { api } from "@lr-notitia/notitia-operator/client";
import IconPreview from "./IconPreview.vue";
import { marked } from "marked";
import markdownToTxt from "markdown-to-txt";

const graphStore = useGraphStore();
const questionsStore = useQuestionsStore();
const domainStore = useDomainStore();
const symbolsStore = useSymbolsStore();
const debugStore = useDebugStore();
const sessionStore = useSessionStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();
const questionHistoryStore = useQuestionHistoryStore();

// we'll use "de" as the language base for rephrasing since it covers kind of all cases
const rephrased_DE = ref<string>("");
const rephrased_translated = ref<string>("");

const compProps = defineProps({
	title: {
		type: String,
		required: true,
	},
	subtitle: {
		type: String,
		required: true,
	},
	noGPTRephrasing: {
		type: Boolean,
		default: false,
	},
	playButton: {
		type: String,
		default: "default",
	},
	showUnknownButton: {
		type: String,
		default: "default",
	},
	instanceTask: {
		type: Object as PropType<InstanceTask>,
		required: true,
	},
	index: {
		type: Number,
		required: true,
	},
});

const templateTask = computed(() => {
	return graphStore.template.task.byId(compProps.instanceTask.template);
});

const outputAttributes = computed(() => {
	return compProps.instanceTask.outputAttributes
		.map((attrId) => graphStore.instance.attribute.byId(attrId) as InstanceAttribute)
		.filter((attr) => attr !== undefined);
});

const openQuestion = computed(() => {
	return questionsStore.tab === "open";
});

const atLeastOneAttributeUnknown = computed(() => {
	return outputAttributes.value.some((attr) => attr.value === "unknown");
});

const note = computed(() => {
	return templateTask.value?.context?.["note"] || "";
});

function openDesignerProcess(event: MouseEvent) {
	event.stopPropagation();
	const protocol = window.location.protocol === "http:" ? "http" : "https";
	const host = `designer.notitia.dev`;
	const { id: domainId } = domainStore.domain;
	const templateTask = graphStore.template.task.byId(compProps.instanceTask.template);
	if (!templateTask) return;
	const templateProcess = graphStore.template.processes.find((p) => p.tasks.includes(templateTask.id));
	if (!templateProcess) return;
	const url = `${protocol}://${host}/${domainId}/template/processes/${templateProcess.id}?task-id=${templateTask.id}`;
	window.open(url, "_blank");
}

function onGroupSelected(expanded: any) {
	if (expanded) questionsStore.currentQuestion = compProps.instanceTask.id;
	else questionsStore.currentQuestion = undefined;
}

function onSet() {
	questionsStore.latestAnsweredQuestion = {
		dataobjectId: compProps.instanceTask.outputDataobject,
		index: compProps.index,
	};
	if (allOutputsSet()) questionsStore.currentQuestion = undefined;
}

async function autofillUnknown(event?: MouseEvent) {
	if (event) event.stopPropagation();

	// Record history for undo: capture previous values before updating
	const historyChanges: { attrId: string; previousValue: any }[] = [];
	outputAttributes.value.forEach((attr) => {
		if (!attr.value || attr.value === "undefined" || attr.value === null) {
			historyChanges.push({ attrId: attr.id, previousValue: attr.value ?? "undefined" });
		}
	});
	if (historyChanges.length > 0) {
		questionHistoryStore.push({
			type: "autofill",
			taskId: compProps.instanceTask.id,
			changes: historyChanges,
		});
	}

	questionsStore.hideFromOpenQuestions.push(compProps.instanceTask.id);
	let promises: Promise<GraphModification>[] = [];

	let attributesToUpdate: { instanceAttrId: string; value: any; source: string }[] = [];

	outputAttributes.value.forEach((attr) => {
		if (!attr.value || attr.value === "undefined" || attr.value === null) {
			promises.push(graphStore.instance.attribute.update(attr.id, "unknown"));
			attributesToUpdate.push({ instanceAttrId: attr.id, value: "unknown", source: "web_ui" });
		}
	});

	if (attributesToUpdate.length > 0) {
		promises.push(graphStore.instance.task.markAnsweredBy(compProps.instanceTask, attributesToUpdate));
	}

	await Promise.all(promises);
	onSet();
}

async function unfillUnknown(event?: MouseEvent) {
	if (event) event.stopPropagation();
	let promises: Promise<GraphModification>[] = [];

	let attributesToUpdate: { instanceAttrId: string; value: any; source: string }[] = [];

	outputAttributes.value.forEach((attr) => {
		if (attr.value === "unknown") {
			promises.push(graphStore.instance.attribute.update(attr.id, "undefined"));
			attributesToUpdate.push({ instanceAttrId: attr.id, value: "undefined", source: "web_ui" });
		}
	});

	if (attributesToUpdate.length > 0) {
		promises.push(graphStore.instance.task.markAnsweredBy(compProps.instanceTask, attributesToUpdate));
	}

	await Promise.all(promises);
	questionsStore.hideFromOpenQuestions = questionsStore.hideFromOpenQuestions.filter(
		(q) => q !== compProps.instanceTask.id,
	);
}

function allOutputsSet(): boolean {
	if (!compProps.instanceTask) return false;
	let outputAttributes = compProps.instanceTask.outputAttributes
		.map((id) => graphStore.instance.attribute.byId(id))
		.filter((a) => a) as InstanceAttribute[];
	return outputAttributes.every(
		(attr) => attr.value !== undefined && attr.value !== null && attr.value !== "undefined",
	);
}

const permamentBlockRephrase = ref(false); // if rephrasing has completed and does not need to be reevaluated

const ist_meldende_person = computed(() => {
	let dataobject = graphStore.instance.dataobject.byId(compProps.instanceTask.outputDataobject);
	if (!dataobject) return false;
	let isDOPatient = dataobject.concept === "27b3b9fb-137b-4108-ad29-da3225a70ee0";
	let isDOMeldend = dataobject.concept === "7c532e69-9787-44b6-9e1b-c2d29012f945";
	if (!isDOPatient && !isDOMeldend) return false;
	if (isDOPatient) {
		let attr_ist_meldende_person = dataobject.consistsOf
			.map((id) => graphStore.instance.attribute.byId(id))
			.find((ia) => ia?.concept === "1df8cfea-852a-4b0a-9ec2-00120261fd59");
		if (!attr_ist_meldende_person) return false;
		let enumTrueSymbolId = "00000000-0000-0000-0000-000000000101";
		let enumItem = graphStore.descriptor.enumItem.byId(attr_ist_meldende_person.value);
		return enumItem?.refersTo === enumTrueSymbolId;
	} else if (isDOMeldend) {
		let attr_meldungsbezug = dataobject.consistsOf
			.map((id) => graphStore.instance.attribute.byId(id))
			.find((ia) => ia?.concept === "a47f92ec-2002-43b3-9d20-4cd6f90955de");

		if (!attr_meldungsbezug) return false;
		let enum_selbst_betroffenSymbolId = "33022efd-b2e1-4d04-ac29-84fcda318503";
		let enumItem = graphStore.descriptor.enumItem.byId(attr_meldungsbezug.value);
		if (enumItem?.refersTo === enum_selbst_betroffenSymbolId) {
			return true;
		}
		if (
			Array.isArray(attr_meldungsbezug.value) &&
			attr_meldungsbezug.value.some((enumItemId) => {
				let enumItem = graphStore.descriptor.enumItem.byId(enumItemId);
				return enumItem?.refersTo === enum_selbst_betroffenSymbolId;
			})
		) {
			return true;
		}
	}

	return false;
});
const DO_Person = computed(() => {
	if (questionsStore.tab === "closed") return;
	let dataobject = graphStore.instance.dataobject.byId(compProps.instanceTask.outputDataobject);
	if (!dataobject) return;
	let isDOPatient = dataobject.concept === "27b3b9fb-137b-4108-ad29-da3225a70ee0";
	let isDOMeldend = dataobject.concept === "7c532e69-9787-44b6-9e1b-c2d29012f945";
	if (!isDOPatient && !isDOMeldend) return;
	return dataobject.composedBy
		.map((id) => graphStore.instance.dataobject.byId(id))
		.find((d) => d && d.concept === "a9445b16-8bc6-426b-9b82-d600724b5d49");
});
const geschlecht = computed(() => {
	if (!DO_Person.value) return;
	let Person_Instance_Attributes = DO_Person.value.consistsOf.map((id) => graphStore.instance.attribute.byId(id));
	let attr_value = Person_Instance_Attributes.find(
		(a) => a?.concept === "404e1ebe-a885-4763-b102-f0c2cb2bbd8f",
	)?.value;
	return attr_value === "70272ece-4702-42a8-846a-0ddd6acf03bd"
		? "männlich"
		: attr_value === "93016950-9a6c-4201-b18b-1f72c0c1ee0a"
		? "weiblich"
		: attr_value === "e9d4a793-66b1-47d6-8c54-eff661f38232"
		? "divers"
		: "unknown";
});

function askGPT() {
	if (compProps.noGPTRephrasing) return;
	if (!templateTask.value) return;
	let question_DE =
		symbolsStore.description({
			id: templateTask.value?.refersTo,
			terminology: templateTask.value?.terminology,
			lang: "de",
		}) || "";
	if (!question_DE) return;
	if (!DO_Person.value) return;
	if (permamentBlockRephrase.value) return;
	const geschlechtIsSet = geschlecht.value !== "unknown" && geschlecht.value !== undefined;
	let canRephrase = geschlechtIsSet || ist_meldende_person.value;
	if (!canRephrase) return;

	if (ist_meldende_person.value || geschlechtIsSet) permamentBlockRephrase.value = true;
	api()
		.gpt()
		.post({
			model: "hf.co/Dampfinchen/google-gemma-3-12b-it-qat-q4_0-gguf-small-fix:latest",
			messages: [
				{
					role: "system",
					content: `
							Du bist ein IT-System, das nur formale Antworten auf gestellte Fragen in der zuvor definierten Weise beantwortet.
							Deine Aufgabe ist es eine Frage auf eine festgelegte Art und Weise umzuformulieren, so dass der Adressat der Frage richtig angesprochen werden kann.
							Dafür muss der Betriff <betroffene Person> in der Frage durch die korrekte Anrede ersetzt werden.

							Zum Schlussfolgern der korrekten Umformulierung der Frage werden die folgenden Informationen zur Verfügung gestellt:
							<ist_selbst_betroffen> (boolean): Information, ob die gefragte Person gleichzeitig auch die betroffene Person ist.
							<geschlecht> (string): Geschlecht der betroffenen Person.

							Beispiel für eine umzuformulierende Frage:
							"Hat die <betroffene Person> Schmerzen?"

							Korrekte Antworten:
							Wenn <ist_selbst_betroffen> == true ist, ist das Geschlecht nicht relevant. Sieze die andere Person immer!:
							"Haben Sie Schmerzen?"

							Wenn <ist_selbst_betroffen> == false, <geschlecht> == "weiblich" ist, ersetze <betroffene Person> durch 'die Betroffene'!:
							"Hat die Betroffene Schmerzen?"

							Wenn <ist_selbst_betroffen> == false, <geschlecht> == "männlich" ist, ersetze <betroffene Person> durch 'der Betroffene'!:
							"Hat der Betroffene Schmerzen?"

							Antwortformat (JSON mit dem key 'text'): {'text': 'Umformulierte Frage'}. Generiere nur das JSON Objekt, ohne die Erklärung/Explanation.

							Nachfolgend nun die Frage, die du umformulieren sollst, sowie die notwendigen Informationen.

							Umzuformulierende Frage: "${question_DE}"

							<ist_selbst_betroffen>: ${ist_meldende_person.value}
							<geschlecht>: ${geschlecht.value}

							Antwortformat (JSON): {"text": "Umformulierte Frage"}.
					`.replace(/^[ \t]+/gm, "").trim(),
				},
			],
			internal_type: "rephrase_question",
			temperature: 1,
			jsonMode: true,
		})
		.then((r) => {
			const gptRes = r;
			if (gptRes === "?") return;
			else rephrased_DE.value = JSON.parse(gptRes).text;
		})
		.catch((err) => console.warn(err));
}

function displayUnknownButton(): boolean {
	// individual settings
	if (compProps.showUnknownButton === "false") return false;
	if (compProps.showUnknownButton === "true") return true;

	// global settings
	const templateWorkspace = graphStore.template.workspace.byId(
		instanceWorkspacesStore.openedWorkspace?.template || "",
	);
	if (templateWorkspace?.context?.show_unknownButton === false) return false;
	if (templateWorkspace?.context?.show_unknownButton === true) return true;

	// default
	return true;
}

watch(
	() => questionsStore.soll_umformulieren_counter,
	(newVal) => {
		// ask gpt when all update api calls are done
		if (newVal > 0) return;
		askGPT();
	},
);

async function translateRephrased() {
	rephrased_translated.value = "";
	if (!rephrased_DE.value) return;
	if (!domainStore.languagePreference) return;
	if (domainStore.languagePreference === "de") {
		rephrased_translated.value = rephrased_DE.value;
		return;
	}
	let last5Messages = sessionStore.session?.messages?.slice(-5)?.map((m) => {
		// return original message texts
		return m?.words?.map((w) => w.value).join(" ") || undefined;
	});
	let translated = await api()
		.deepl()
		.post({
			text: rephrased_DE.value,
			sourceLang: "de",
			targetLang: domainStore.languagePreference,
			context: last5Messages ? JSON.stringify(last5Messages) : undefined,
		});
	rephrased_translated.value = translated || "";
}

function displayPlayButton(): boolean {
	// individual settings
	if (compProps.playButton === "show") return true;
	if (compProps.playButton === "hide") return false;

	// global settings
	const templateWorkspace = graphStore.template.workspace.byId(
		instanceWorkspacesStore.openedWorkspace?.template || "",
	);
	if (templateWorkspace?.context?.show_askQuestionButton === false) return false;
	if (templateWorkspace?.context?.show_askQuestionButton === true) return true;

	// default
	return true;
}

watch(
	() => [rephrased_DE.value, domainStore.languagePreference],
	() => translateRephrased(),
);

onMounted(() => {
	askGPT();

	window.addEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.addEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
onUnmounted(() => {
	window.removeEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.removeEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
</script>

<style scoped lang="scss">
:deep(.v-expansion-panel-title) {
	padding-left: 16px;
}
:deep(.v-expansion-panel-text__wrapper) {
	padding-left: 16px;
	padding-right: 16px;
}

.markdown-content {
	:deep(p) {
		margin-bottom: 0px;
	}
	:deep(h1) {
		font-size: 1.35rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(h2) {
		font-size: 1.25rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(h3) {
		font-size: 1.15rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(h4),
	:deep(h5),
	:deep(h6) {
		font-size: 1.05rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(ul),
	:deep(ol) {
		padding-left: 20px;
		margin-bottom: 8px;
	}

	:deep(table) {
		border-collapse: collapse;
		margin: 15px 0;
	}
	:deep(th),
	:deep(td) {
		border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
		padding: 6px 13px;
	}

	:deep(blockquote) {
		border-left: 4px solid rgba(var(--v-theme-on-surface), 0.2);
		padding-left: 15px;
		color: rgba(var(--v-theme-on-surface), 0.7);
		margin: 15px 0;
	}

	:deep(pre) {
		background-color: rgba(var(--v-theme-on-surface), 0.05);
		padding: 10px;
		border-radius: 4px;
		overflow: auto;
		margin: 15px 0;
		font-family: monospace;

		code {
			background-color: transparent;
			padding: 0;
		}
	}

	:deep(code) {
		background-color: rgba(var(--v-theme-on-surface), 0.05);
		padding: 2px 4px;
		border-radius: 3px;
		font-family: monospace;
	}
}
</style>
